import { Component, DestroyRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { firstValueFrom } from 'rxjs';

import { SiteBudgetDropdownComponent } from '@pages/investigator/site-costs/site/tabs/site-budget/components/site-budget-dropdown/site-budget-dropdown.component';
import { SiteBudgetService } from '@pages/investigator/site-costs/site/tabs/site-budget/services/site-budget.service';

import { FileManagerUploadedFilesComponent } from '@features/file-manager-uploaded-files/file-manager-uploaded-files.component';

import { FileManagerComponent } from '@shared/components/file-manager/file-manager.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { InputComponent } from '@shared/components/input/input.component';
import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';
import { FormErrorDirective } from '@shared/directives/form-error.directive';
import { DocumentLibraryService } from '@shared/services/document-library.service';
import {
  DocumentType,
  EntityType,
  EventType,
  listSiteBudgetVersionsQuery,
} from '@shared/services/gql.service';
import { AuthQuery } from '@shared/store/auth/auth.query';
import { EventService } from '@shared/store/event/event.service';
import { MainQuery } from '@shared/store/main/main.query';
import { Option } from '@shared/types/components.type';
import { isDuplicateError } from '@shared/utils';
import { Maybe } from '@shared/utils/utils';

interface CreateAmendmentForm {
  amendmentName: null | string;
  protocolVersion: null | string;
  reason: null | string;
  siteBudgetVersion: null | string;
}

@Component({
  imports: [
    ReactiveFormsModule,
    NgSelectModule,
    InputComponent,
    FileManagerComponent,
    IconComponent,
    FileManagerUploadedFilesComponent,
    FormErrorDirective,
    SiteBudgetDropdownComponent,
  ],
  styles: [
    `
      :host .site-budget-amendment-modal {
        .select {
          height: auto;
        }
      }
    `,
  ],
  templateUrl: './site-budget-amendment-modal.component.html',
})
export class SiteBudgetAmendmentModalComponent implements OnInit {
  destroyRef = inject(DestroyRef);
  public ref = inject(
    CustomOverlayRef<
      unknown,
      { protocolVersion: string; protocolVersionList: Option[]; siteId: string }
    >
  );

  private authQuery = inject(AuthQuery);
  private documentLibrary = inject(DocumentLibraryService);
  private eventService = inject(EventService);
  private fb = inject(FormBuilder);
  private mainQuery = inject(MainQuery);
  private siteBudgetService = inject(SiteBudgetService);

  selectedProtocolVersion = signal<null | string>(this.ref.data?.protocolVersion || null);
  selectedSiteId = signal<null | string>(this.ref.data?.siteId || null);

  createAmendmentForm = this.fb.group<CreateAmendmentForm>({
    amendmentName: null,
    protocolVersion: this.ref.data?.protocolVersion || null,
    reason: null,
    siteBudgetVersion: null,
  });
  disabledSiteBudgetTooltip = '';
  documentTypeOptions: Option[] = [];
  @ViewChild(FileManagerComponent) fileManager: FileManagerComponent | undefined;
  @ViewChild('siteBudgetNameInput') siteBudgetNameInput!: InputComponent;

  ngOnInit(): void {
    this.documentTypeOptions = this.documentLibrary.getDocumentOptions();

    this.createAmendmentForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const { protocolVersion } = this.createAmendmentForm.getRawValue();

        if (protocolVersion !== this.selectedProtocolVersion()) {
          this.selectedProtocolVersion.set(protocolVersion ?? null);
          this.createAmendmentForm.controls.siteBudgetVersion.setValue(null);
        }
      });
  }

  async createAmendment() {
    this.revalidateFields();

    if (this.createAmendmentForm.invalid) {
      return;
    }

    const { amendmentName, protocolVersion, reason, siteBudgetVersion } =
      this.createAmendmentForm.getRawValue();
    const { data: siteBudget, errors } = await firstValueFrom(
      this.siteBudgetService.createSiteBudgetVersion$({
        copy_from_site_budget_version_id: siteBudgetVersion,
        files_uploaded:
          this.fileManager?.fileQuery
            .getAll()
            .map((x) => x.fileName)
            .join(', ') || '—',
        message: reason,
        name: <string>amendmentName,
        patient_protocol_version_id: <string>protocolVersion,
        site_id: this.ref.data?.siteId || '',
      })
    );

    if (errors.length && errors.some((error) => isDuplicateError(error))) {
      this.siteBudgetNameInput.fc.setErrors({ duplicate_site_budget: true });

      return;
    }

    let reasonId: Maybe<string> = null;

    if (siteBudget) {
      await this.uploadFiles(siteBudget.id);

      if (reason) {
        const { data } = await firstValueFrom(
          this.siteBudgetService.createNote$(reason, siteBudget.id)
        );

        reasonId = data?.id;
      }
    }

    this.ref.close({
      siteBudget: {
        ...siteBudget,
        documents:
          this.fileManager?.fileQuery.getAll()?.map((file) => ({ ...file, name: file.fileName })) ||
          [],
        notes: reason ? [{ id: reasonId, message: reason }] : [],
      },
    });
  }

  revalidateFields() {
    Object.values(this.createAmendmentForm.controls).forEach((control) => {
      control.markAsTouched();
      control.updateValueAndValidity();
    });
  }

  siteBudgetOptionsChanged(options: listSiteBudgetVersionsQuery[]): void {
    if (options.length > 0) {
      this.createAmendmentForm.controls.siteBudgetVersion.enable();
      this.disabledSiteBudgetTooltip = '';
    } else {
      this.createAmendmentForm.controls.siteBudgetVersion.disable();
      this.disabledSiteBudgetTooltip = 'No other Site Budget Versions are available to copy from.';
    }
  }

  async uploadFiles(siteBudgetId: string) {
    const { siteId } = this.ref.data || {};

    if (!this.fileManager || !siteId) return;

    const { trialKey } = this.mainQuery.getValue();

    await this.fileManager.fileService.uploadFiles({
      filterLocalFilesByUploadedFlag: true,
      insertDocument: true,
      is_metadata_editable: false,
      metadata: {
        documentType: DocumentType.DOCUMENT_SITE_AGREEMENTS,
        entity_id: siteBudgetId,
        entity_type_id: EntityType.SITE_BUDGET_VERSION,
        site: siteId,
      },
      overrideFilePath: `trials/${trialKey}/sites/${siteId}/${siteBudgetId}/contracts/`,
      removeFilesOnPartialFailure: true,
      showSuccess: true,
    });

    const files = this.fileManager.fileQuery.getAll();
    const { reason } = this.createAmendmentForm.getRawValue();

    this.eventService.processEvent$({
      entity_id: '',
      entity_type: EntityType.DOCUMENT,
      payload: JSON.stringify({
        file_names: files.map((file) => file.fileName).join(', '),
        reason_for_amendment: reason,
        site_budget_version_id: siteBudgetId,
        site_id: siteId,
        uploaded_by: `${this.authQuery.getFullName()} ${this.authQuery.getEmail()}`,
      }),
      type: EventType.SITE_CONTRACT_UPLOADED,
    });
  }
}
